import React, { useMemo, useState } from "react"
import { Navigate, Link } from "react-router-dom"
import moment from "moment"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import LoadingWrapper from "~/library/LoadingWrapper"
import PageTitle from "~/library/PageTitle"
import Pagination from "~/library/Pagination"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceStripeConnectedPayouts } from "~/routes"
import getAppConfig from "~/utils/getAppConfig"
import { formatCurrency } from "~/utils/helpers"
import type { StripeConnectedPayout } from "~/types/StripeConnectedPayout"

const PayoutList = () => {
  const { user, venueSlug } = getAppConfig()
  const [filters, setFilters] = useState({ currency: "", status: "" })

  const {
    data: payouts,
    isLoading,
    metadata,
    refetch,
  } = useFetchResource<{ payouts: StripeConnectedPayout[] }, "payouts">(
    apiWorkspaceStripeConnectedPayouts.index.path({ venueSlug }),
    "payouts",
    { page: "1" }
  )

  const queryParams = (overrides: Record<string, string> = {}) => ({
    page: "1",
    ...filters,
    ...overrides,
  })

  const updateFilter = (name: keyof typeof filters, value: string) => {
    const nextFilters = { ...filters, [name]: value }
    setFilters(nextFilters)
    refetch({ page: "1", ...nextFilters })
  }

  const columns = useMemo(
    () => [
      {
        key: "arrivalDate" as const,
        header: "Payout Date",
        formatter: (value: string | null) => (value ? moment(value).format("MMM Do YYYY") : "—"),
      },
      {
        key: "stripePayoutId" as const,
        header: "Stripe Payout",
        formatter: (value: string, payout: StripeConnectedPayout) => (
          <Link to={`/payments/payouts/${payout.id}`}>{value}</Link>
        ),
      },
      {
        key: "status" as const,
        header: "Status",
        formatter: (value: string) => value.replace("_", " "),
      },
      {
        key: "amountCents" as const,
        header: "Stripe Payout Amount",
        formatter: (value: number, payout: StripeConnectedPayout) =>
          formatCurrency({ cents: value, currency_iso: payout.currency }),
      },
      {
        key: "grossAmountCents" as const,
        header: "Gross Catch Payments",
        formatter: (value: number, payout: StripeConnectedPayout) =>
          payout.automatic === false
            ? "—"
            : formatCurrency({ cents: value, currency_iso: payout.currency }),
      },
      {
        key: "processingFeeCents" as const,
        header: "Catch Processing Fee",
        formatter: (value: number, payout: StripeConnectedPayout) =>
          payout.automatic === false
            ? "—"
            : formatCurrency({ cents: value, currency_iso: payout.currency }),
      },
      {
        key: "netAmountCents" as const,
        header: "Net Catch Proceeds",
        formatter: (value: number, payout: StripeConnectedPayout) =>
          payout.automatic === false
            ? "—"
            : formatCurrency({ cents: value, currency_iso: payout.currency }),
      },
      {
        key: "matchedPaymentCount" as const,
        header: "Payments",
        formatter: (value: number, payout: StripeConnectedPayout) =>
          payout.automatic === false ? "—" : value,
      },
    ],
    []
  )

  if (user.role !== "internal") return <Navigate to="/payments" replace />

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle title="Payouts">
        <Link className="btn btn-outline-secondary" to="/payments">
          Payments
        </Link>
      </PageTitle>

      <div className="d-flex gap-2 justify-content-end mb-3">
        <select
          className="form-select w-auto"
          value={filters.status}
          onChange={(event) => updateFilter("status", event.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_transit">In transit</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="canceled">Canceled</option>
        </select>
        <select
          className="form-select w-auto"
          value={filters.currency}
          onChange={(event) => updateFilter("currency", event.target.value)}
        >
          <option value="">All currencies</option>
          <option value="usd">USD</option>
          <option value="cad">CAD</option>
        </select>
      </div>

      <CatchCard>
        <p className="text-muted mb-3">
          Stripe payout amount is the venue&apos;s complete bank payout. Catch payment totals
          include only matched Catch payments.
        </p>
        <CatchTable
          data={payouts}
          columns={columns}
          emptyMessage="No synchronized payouts"
          rowKey={(payout) => payout.id}
        />
        {metadata && (
          <Pagination
            metadata={metadata}
            onPageChange={(page) => refetch(queryParams({ page: String(page) }))}
          />
        )}
      </CatchCard>
    </LoadingWrapper>
  )
}

export default PayoutList
